import { Directive, effect, ElementRef, inject, input } from '@angular/core';

/** Adds a brief scale-pulse to the host element whenever the bound value
    changes meaningfully. Used on the delta-badge so a refresh / sign-flip
    is visually noticeable instead of just a quiet color change. */
@Directive({
  selector: '[softDeltaFlip]',
  standalone: true,
})
export class DeltaFlipDirective {
  readonly value = input.required<number | null | undefined>({
    alias: 'softDeltaFlip',
  });
  /** When false, suppresses the scale-pulse on value change. Use for live
      tickers where the flip would replay every tick. */
  readonly animate = input<boolean>(true);

  private readonly el = inject(ElementRef<HTMLElement>);
  private previousValue: number | null | undefined = undefined;
  private hasFlippedInitial = false;

  constructor() {
    effect(() => {
      const v = this.value();

      const isNoSignal = (x: number | null | undefined): boolean =>
        x === null || x === undefined || Number.isNaN(x);

      if (!this.hasFlippedInitial) {
        if (!isNoSignal(v)) {
          this.hasFlippedInitial = true;
          const node = this.el.nativeElement;
          node.classList.remove('ds-kpi-delta-flip');
          void node.offsetWidth;
          node.classList.add('ds-kpi-delta-flip');
        }
        this.previousValue = v;
        return;
      }

      const changed = isNoSignal(v) || isNoSignal(this.previousValue)
        ? isNoSignal(v) !== isNoSignal(this.previousValue)
        : v !== this.previousValue;

      if (changed && !isNoSignal(v) && this.animate()) {
        const node = this.el.nativeElement;
        node.classList.remove('ds-kpi-delta-flip');
        void node.offsetWidth;
        node.classList.add('ds-kpi-delta-flip');
      }
      this.previousValue = v;
    });
  }
}
